import { useEffect, useRef, useState } from "react";
import { useAuth, startGoogleLogin } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Bold, Italic, Heading1, Heading2, Heading3, Quote, List, ListOrdered, Link2, Image as ImageIcon, Eye, Edit3, X, UserCircle2, Star, Gamepad2, Radio as RadioIcon, Music, ArrowUp, ArrowDown, BookOpen, MapPin, Archive, ArchiveRestore } from "lucide-react";
import MarkdownArticle from "../components/tavern/MarkdownArticle";
import ImageUploader from "../components/tavern/ImageUploader";
import AudioUploader from "../components/tavern/AudioUploader";
import { GAME_STATUSES, statusLabel } from "../constants/games";

const CATEGORIES = [
  { value: "notice-board", label: "Notice Board (updates, news, projects)" },
  { value: "adventures", label: "Adventures (gaming journals & playthroughs)" },
  { value: "radio", label: "Nocturne Radio (lore & broadcasts)" },
  { value: "second-life", label: "Second Life (sim journals)" },
  { value: "star-charts", label: "Star Charts (curiosities, science, folklore)" },
  { value: "life-updates", label: "Life Updates (personal notes)" },
];

const EMPTY_POST = { title: "", excerpt: "", body: "", category: "notice-board", image_url: "", tags: "" };
const EMPTY_CHARACTER = { name: "", role: "", description: "", image_url: "", order: 0, is_primary: false };
const EMPTY_GAME = { name: "", status: "playing", rating: 0, review: "", image_url: "" };
const EMPTY_TRACK = { title: "", dj: "", lore: "", audio_url: "" };

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [characterForm, setCharacterForm] = useState(EMPTY_CHARACTER);
  const [editingCharId, setEditingCharId] = useState(null);
  const [games, setGames] = useState([]);
  const [gameForm, setGameForm] = useState(EMPTY_GAME);
  const [editingGameId, setEditingGameId] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [trackForm, setTrackForm] = useState(EMPTY_TRACK);
  const [editingTrackId, setEditingTrackId] = useState(null);
  const [guestbook, setGuestbook] = useState([]);
  const [postForm, setPostForm] = useState(EMPTY_POST);
  const [editingId, setEditingId] = useState(null);
  const [galleryForm, setGalleryForm] = useState({ title: "", caption: "", image_url: "", category: "creative" });
  const [tab, setTab] = useState("write"); // write | preview
  const [insertOpen, setInsertOpen] = useState(false); // markdown image-insert dialog
  const bodyRef = useRef(null);

  useEffect(() => {
    if (user) {
      api.get("/posts").then(r => setPosts(r.data));
      api.get("/gallery").then(r => setGallery(r.data));
      api.get("/characters").then(r => setCharacters(r.data)).catch(() => {});
      api.get("/games").then(r => setGames(r.data)).catch(() => {});
      api.get("/radio").then(r => setTracks(r.data)).catch(() => {});
      api.get("/guestbook/all").then(r => setGuestbook(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading) return <div className="page-container"><div style={{color:"var(--muted)"}}>Checking session…</div></div>;

  if (!user) {
    return (
      <div className="page-container fade-in" data-testid="admin-login">
        <div className="page-eyebrow">keeper's quarters</div>
        <h1 className="page-title">Sign In</h1>
        <p className="page-sub">Only the tavern keeper may post new notices, journals, gallery items, and broadcasts.</p>
        <div className="parchment-card p-8 mt-8 max-w-lg">
          <p className="text-sm" style={{ color: "var(--muted)" }}>Sign in with your Google account. The first account to sign in becomes the keeper.</p>
          <button className="gold-btn mt-5 glow-pulse" onClick={startGoogleLogin} data-testid="google-login-btn">
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="page-container fade-in">
        <h1 className="page-title">Welcome, {user.name}</h1>
        <p className="page-sub">You're signed in, but you're not the keeper. Enjoy the rooms.</p>
        <button className="gold-btn mt-5" onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn">Sign Out</button>
      </div>
    );
  }

  // ---------- markdown toolbar helpers ----------
  const wrapSelection = (before, after = before, placeholder = "text") => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const value = el.value;
    const selected = value.slice(start, end) || placeholder;
    const next = value.slice(0, start) + before + selected + after + value.slice(end);
    setPostForm(f => ({ ...f, body: next }));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = start + before.length;
      el.selectionEnd = start + before.length + selected.length;
    });
  };

  const prefixLines = (prefix) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const value = el.value;
    const selStart = value.lastIndexOf("\n", start - 1) + 1;
    const before = value.slice(0, selStart);
    const block = value.slice(selStart, end);
    const after = value.slice(end);
    const blockLines = (block || "line").split("\n");
    const transformed = blockLines.map((l, i) => {
      if (typeof prefix === "function") return prefix(l, i);
      return prefix + l;
    }).join("\n");
    setPostForm(f => ({ ...f, body: before + transformed + after }));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = selStart;
      el.selectionEnd = selStart + transformed.length;
    });
  };

  const insertLink = () => {
    const url = window.prompt("Link URL", "https://");
    if (!url) return;
    const el = bodyRef.current;
    const sel = el ? el.value.slice(el.selectionStart, el.selectionEnd) : "";
    wrapSelection("[", `](${url})`, sel || "link text");
  };

  const insertImage = () => {
    // Opens dialog with "Upload" or "Paste URL" options
    setInsertOpen(true);
  };

  const insertMarkdownImage = (url, alt = "image") => {
    const el = bodyRef.current;
    if (!el) {
      setPostForm(f => ({ ...f, body: f.body + `\n\n![${alt}](${url})\n\n` }));
      return;
    }
    const insertText = `\n\n![${alt}](${url})\n\n`;
    const value = el.value;
    const start = el.selectionStart;
    setPostForm(f => ({ ...f, body: value.slice(0, start) + insertText + value.slice(start) }));
  };

  // ---------- form actions ----------
  const submitPost = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...postForm,
        excerpt: postForm.excerpt || undefined,
        tags: postForm.tags ? postForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      };
      if (editingId) {
        const r = await api.put(`/posts/${editingId}`, payload);
        setPosts(posts.map(p => p.id === editingId ? r.data : p));
        toast.success("Entry updated");
      } else {
        const r = await api.post("/posts", payload);
        setPosts([r.data, ...posts]);
        toast.success("Entry added");
      }
      setPostForm(EMPTY_POST);
      setEditingId(null);
      setTab("write");
    } catch (err) {
      toast.error("Could not save entry");
    }
  };

  const startEdit = (post) => {
    setEditingId(post.id);
    setPostForm({
      title: post.title || "",
      excerpt: post.excerpt || "",
      body: post.body || "",
      category: post.category || "notice-board",
      image_url: post.image_url || "",
      tags: (post.tags || []).join(", "),
    });
    setTab("write");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setPostForm(EMPTY_POST);
  };

  const deletePost = async (id) => {
    try {
      await api.delete(`/posts/${id}`);
      setPosts(posts.filter(p => p.id !== id));
      if (editingId === id) cancelEdit();
      toast.success("Entry archived");
    } catch { toast.error("Delete failed"); }
  };

  const submitGallery = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/gallery", galleryForm);
      setGallery([r.data, ...gallery]);
      setGalleryForm({ title: "", caption: "", image_url: "", category: "creative" });
      toast.success("Photograph framed");
    } catch { toast.error("Could not save"); }
  };

  const deleteGallery = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      setGallery(gallery.filter(g => g.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  // ---------- character handlers ----------
  const submitCharacter = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...characterForm,
        order: Number(characterForm.order) || 0,
      };
      if (editingCharId) {
        const r = await api.put(`/characters/${editingCharId}`, payload);
        setCharacters(characters.map(c => c.id === editingCharId ? r.data : c));
        toast.success("Character updated");
      } else {
        const r = await api.post("/characters", payload);
        setCharacters([...characters, r.data]);
        toast.success("Character added");
      }
      setCharacterForm(EMPTY_CHARACTER);
      setEditingCharId(null);
    } catch {
      toast.error("Could not save character");
    }
  };

  const startEditCharacter = (c) => {
    setEditingCharId(c.id);
    setCharacterForm({
      name: c.name || "",
      role: c.role || "",
      description: c.description || "",
      image_url: c.image_url || "",
      order: c.order ?? 0,
      is_primary: !!c.is_primary,
    });
    document.getElementById("character-editor")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEditCharacter = () => {
    setEditingCharId(null);
    setCharacterForm(EMPTY_CHARACTER);
  };

  const deleteCharacter = async (id) => {
    if (!window.confirm("Remove this character profile?")) return;
    try {
      await api.delete(`/characters/${id}`);
      setCharacters(characters.filter(c => c.id !== id));
      if (editingCharId === id) cancelEditCharacter();
      toast.success("Character removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  // ---------- game handlers ----------
  const submitGame = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: gameForm.name,
        status: gameForm.status,
        rating: gameForm.rating ? Number(gameForm.rating) : null,
        review: gameForm.review || null,
        image_url: gameForm.image_url || null,
      };
      if (editingGameId) {
        const r = await api.put(`/games/${editingGameId}`, payload);
        setGames(games.map(g => g.id === editingGameId ? r.data : g));
        toast.success("Game updated");
      } else {
        const r = await api.post("/games", payload);
        setGames([...games, r.data]);
        toast.success("Game added");
      }
      setGameForm(EMPTY_GAME);
      setEditingGameId(null);
    } catch {
      toast.error("Could not save game");
    }
  };

  const startEditGame = (g) => {
    setEditingGameId(g.id);
    setGameForm({
      name: g.name || "",
      status: g.status || "playing",
      rating: g.rating || 0,
      review: g.review || "",
      image_url: g.image_url || "",
    });
    document.getElementById("game-editor")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEditGame = () => {
    setEditingGameId(null);
    setGameForm(EMPTY_GAME);
  };

  const deleteGame = async (id) => {
    if (!window.confirm("Remove this game from the library?")) return;
    try {
      await api.delete(`/games/${id}`);
      setGames(games.filter(g => g.id !== id));
      if (editingGameId === id) cancelEditGame();
      toast.success("Game removed");
    } catch { toast.error("Delete failed"); }
  };

  const moveGame = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= games.length) return;
    const reordered = [...games];
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    setGames(reordered);
    try {
      const r = await api.post("/games/reorder", { ids: reordered.map(g => g.id) });
      setGames(r.data);
    } catch { toast.error("Reorder failed"); }
  };

  // ---------- radio handlers ----------
  const submitTrack = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: trackForm.title,
        dj: trackForm.dj || "Nocturne",
        lore: trackForm.lore || null,
        audio_url: trackForm.audio_url || null,
      };
      if (editingTrackId) {
        const r = await api.put(`/radio/${editingTrackId}`, payload);
        setTracks(tracks.map(t => t.id === editingTrackId ? r.data : t));
        toast.success("Track updated");
      } else {
        const r = await api.post("/radio", payload);
        setTracks([...tracks, r.data]);
        toast.success("Track added");
      }
      setTrackForm(EMPTY_TRACK);
      setEditingTrackId(null);
    } catch {
      toast.error("Could not save track");
    }
  };

  const startEditTrack = (t) => {
    setEditingTrackId(t.id);
    setTrackForm({
      title: t.title || "",
      dj: t.dj || "",
      lore: t.lore || "",
      audio_url: t.audio_url || "",
    });
    document.getElementById("radio-editor")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEditTrack = () => {
    setEditingTrackId(null);
    setTrackForm(EMPTY_TRACK);
  };

  const deleteTrack = async (id) => {
    if (!window.confirm("Remove this track from the setlist?")) return;
    try {
      await api.delete(`/radio/${id}`);
      setTracks(tracks.filter(t => t.id !== id));
      if (editingTrackId === id) cancelEditTrack();
      toast.success("Track removed");
    } catch { toast.error("Delete failed"); }
  };

  const moveTrack = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= tracks.length) return;
    const reordered = [...tracks];
    [reordered[index], reordered[next]] = [reordered[next], reordered[index]];
    setTracks(reordered);
    try {
      const r = await api.post("/radio/reorder", { ids: reordered.map(t => t.id) });
      setTracks(r.data);
    } catch { toast.error("Reorder failed"); }
  };

  // ---------- guest book handlers ----------
  const archiveEntry = async (id) => {
    try {
      const r = await api.post(`/guestbook/${id}/archive`);
      setGuestbook(guestbook.map(g => g.id === id ? r.data : g));
      toast.success("Entry archived");
    } catch { toast.error("Could not archive"); }
  };

  const unarchiveEntry = async (id) => {
    try {
      const r = await api.post(`/guestbook/${id}/unarchive`);
      setGuestbook(guestbook.map(g => g.id === id ? r.data : g));
      toast.success("Entry restored");
    } catch { toast.error("Could not restore"); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Permanently delete this guest book entry? This cannot be undone.")) return;
    try {
      await api.delete(`/guestbook/${id}`);
      setGuestbook(guestbook.filter(g => g.id !== id));
      toast.success("Entry deleted");
    } catch { toast.error("Delete failed"); }
  };

  const input = "w-full px-3 py-2 rounded-sm bg-[#1a0a2e] border text-[color:var(--silver)] focus:outline-none focus:ring-1 focus:ring-[color:var(--gold)]";
  const inputStyle = { borderColor: "rgba(212,175,55,0.3)" };

  const ToolbarBtn = ({ onClick, label, children, testid }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testid}
      className="h-9 w-9 grid place-items-center rounded-sm border transition-colors hover:bg-[rgba(212,175,55,0.12)]"
      style={{ borderColor: "rgba(212,175,55,0.25)", color: "var(--gold)" }}
    >
      {children}
    </button>
  );

  return (
    <div className="page-container fade-in" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="page-eyebrow">keeper's quarters</div>
          <h1 className="page-title">Welcome back, {user.name.split(" ")[0]}</h1>
        </div>
        <button className="gold-btn" onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn">Sign Out</button>
      </div>

      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-8 mt-10">
        {/* POST EDITOR */}
        <div className="parchment-card p-6" data-testid="post-form-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="tavern-heading text-2xl gold-text">
                {editingId ? "Edit Entry" : "New Entry"}
              </h2>
              <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                Markdown supported: <code>**bold**</code>, <code>*italic*</code>, <code># heading</code>, <code>&gt; quote</code>, <code>- list</code>, <code>[link](url)</code>, <code>![image](url)</code>.
              </p>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setTab("write")}
                className="gold-btn !py-1 !px-3 text-xs"
                style={tab==="write"?{background:"var(--gold)", color:"var(--bg-base)"}:{}}
                data-testid="tab-write">
                <Edit3 className="h-3 w-3" /> Write
              </button>
              <button type="button" onClick={() => setTab("preview")}
                className="gold-btn !py-1 !px-3 text-xs"
                style={tab==="preview"?{background:"var(--gold)", color:"var(--bg-base)"}:{}}
                data-testid="tab-preview">
                <Eye className="h-3 w-3" /> Preview
              </button>
            </div>
          </div>

          <form className="mt-5 space-y-3" onSubmit={submitPost}>
            <input className={input} placeholder="Title" value={postForm.title}
              onChange={e => setPostForm({ ...postForm, title: e.target.value })}
              required data-testid="post-title" style={inputStyle} />

            <div className="grid sm:grid-cols-2 gap-3">
              <select className={input} value={postForm.category}
                onChange={e => setPostForm({ ...postForm, category: e.target.value })}
                data-testid="post-category" style={inputStyle}>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <input className={input} placeholder="Tags (comma separated)"
                value={postForm.tags}
                onChange={e => setPostForm({ ...postForm, tags: e.target.value })}
                data-testid="post-tags" style={inputStyle} />
            </div>

            {/* Featured Image */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="page-eyebrow" style={{fontSize:"0.7rem"}}>Featured Image</label>
                {postForm.image_url && (
                  <button type="button" className="text-xs underline" style={{color:"var(--muted)"}}
                    onClick={() => setPostForm({...postForm, image_url: ""})} data-testid="clear-featured-image">
                    clear
                  </button>
                )}
              </div>
              {postForm.image_url ? (
                <div className="relative" data-testid="post-image-preview">
                  <img src={postForm.image_url} alt="Featured preview"
                    className="w-full max-h-72 object-cover rounded-sm"
                    style={{ border: "1px solid rgba(212,175,55,0.25)" }}
                    onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                  />
                  <span className="absolute top-2 left-2 text-[0.65rem] px-2 py-0.5 rounded-sm"
                    style={{ background: "rgba(0,0,0,0.6)", color: "var(--gold)" }}>featured image preview</span>
                </div>
              ) : (
                <ImageUploader
                  label="Upload Featured Image"
                  testid="featured-uploader"
                  onUploaded={(d) => setPostForm(f => ({ ...f, image_url: d.url }))}
                />
              )}
              <input className={`${input} mt-2 text-xs`}
                placeholder="…or paste an external image URL (Flickr, Unsplash, etc.)"
                value={postForm.image_url}
                onChange={e => setPostForm({ ...postForm, image_url: e.target.value })}
                data-testid="post-image" style={inputStyle} />
            </div>

            {/* Excerpt */}
            <div>
              <textarea className={input} rows={2}
                placeholder="Excerpt (optional — auto-generated from the first 180 characters of the body if left blank)"
                value={postForm.excerpt}
                onChange={e => setPostForm({ ...postForm, excerpt: e.target.value })}
                data-testid="post-excerpt" style={inputStyle} />
              <div className="text-[0.7rem] mt-1" style={{ color: "var(--muted)" }}>
                {postForm.excerpt
                  ? `${postForm.excerpt.length} character excerpt`
                  : "Auto-generated excerpt will be created on save."}
              </div>
            </div>

            {/* Body with markdown toolbar */}
            <div>
              <div className="flex flex-wrap gap-1 p-2 rounded-sm border border-b-0"
                style={{ borderColor: "rgba(212,175,55,0.25)", background: "rgba(0,0,0,0.2)" }}
                data-testid="md-toolbar">
                <ToolbarBtn onClick={() => prefixLines("# ")} label="Heading 1" testid="md-h1"><Heading1 className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => prefixLines("## ")} label="Heading 2" testid="md-h2"><Heading2 className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => prefixLines("### ")} label="Heading 3" testid="md-h3"><Heading3 className="h-4 w-4" /></ToolbarBtn>
                <div className="w-px h-7 self-center mx-1" style={{ background: "rgba(212,175,55,0.25)" }} />
                <ToolbarBtn onClick={() => wrapSelection("**", "**", "bold")} label="Bold" testid="md-bold"><Bold className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => wrapSelection("*", "*", "italic")} label="Italic" testid="md-italic"><Italic className="h-4 w-4" /></ToolbarBtn>
                <div className="w-px h-7 self-center mx-1" style={{ background: "rgba(212,175,55,0.25)" }} />
                <ToolbarBtn onClick={() => prefixLines("> ")} label="Blockquote" testid="md-quote"><Quote className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => prefixLines("- ")} label="Bullet list" testid="md-ul"><List className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => prefixLines((_, i) => `${i + 1}. `)} label="Numbered list" testid="md-ol"><ListOrdered className="h-4 w-4" /></ToolbarBtn>
                <div className="w-px h-7 self-center mx-1" style={{ background: "rgba(212,175,55,0.25)" }} />
                <ToolbarBtn onClick={insertLink} label="Insert link" testid="md-link"><Link2 className="h-4 w-4" /></ToolbarBtn>
                <ToolbarBtn onClick={insertImage} label="Insert image" testid="md-image"><ImageIcon className="h-4 w-4" /></ToolbarBtn>
              </div>

              {tab === "write" ? (
                <textarea ref={bodyRef}
                  className={`${input} font-mono text-sm`}
                  rows={16}
                  placeholder={`Write your post in Markdown.\n\n# A heading\n\nA paragraph with **bold** and *italic* words.\n\n> A blockquote.\n\n- A list item\n- Another item\n\n[A link](https://example.com)\n\n![Image](https://...)`}
                  value={postForm.body}
                  onChange={e => setPostForm({ ...postForm, body: e.target.value })}
                  required
                  data-testid="post-body"
                  style={{ ...inputStyle, borderTopLeftRadius: 0, borderTopRightRadius: 0 }} />
              ) : (
                <div className="p-5 rounded-sm border border-t-0 min-h-[300px]"
                  style={{ borderColor: "rgba(212,175,55,0.3)", background: "rgba(0,0,0,0.15)" }}
                  data-testid="post-preview">
                  {postForm.image_url && (
                    <img src={postForm.image_url} alt="" className="w-full max-h-72 object-cover rounded-sm mb-4"
                      style={{ border: "1px solid rgba(212,175,55,0.25)" }} />
                  )}
                  {postForm.title && <h2 className="tavern-heading text-3xl gold-text">{postForm.title}</h2>}
                  {postForm.excerpt && <p className="tavern-script text-xl mt-2" style={{ color: "var(--orchid-light)" }}>{postForm.excerpt}</p>}
                  <div className="gold-rule my-4" />
                  <MarkdownArticle className="prose-wide">{postForm.body || "_Start writing to see the preview._"}</MarkdownArticle>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" className="gold-btn" data-testid="post-submit">
                {editingId ? "Save Changes" : "Publish"}
              </button>
              {editingId && (
                <button type="button" className="gold-btn" onClick={cancelEdit} data-testid="post-cancel"
                  style={{ borderColor: "var(--muted)", color: "var(--muted)" }}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        {/* RIGHT COL: Gallery form */}
        <div className="parchment-card p-6 h-fit" data-testid="gallery-form-card">
          <h2 className="tavern-heading text-2xl gold-text">Add to Gallery</h2>
          <form className="mt-5 space-y-3" onSubmit={submitGallery}>
            <input className={input} placeholder="Title" value={galleryForm.title}
              onChange={e => setGalleryForm({ ...galleryForm, title: e.target.value })}
              required data-testid="gallery-title" style={inputStyle} />
            <input className={input} placeholder="Caption" value={galleryForm.caption}
              onChange={e => setGalleryForm({ ...galleryForm, caption: e.target.value })}
              data-testid="gallery-caption" style={inputStyle} />
            <input className={input} placeholder="Image URL (or upload below)" value={galleryForm.image_url}
              onChange={e => setGalleryForm({ ...galleryForm, image_url: e.target.value })}
              data-testid="gallery-image" style={inputStyle} />
            <ImageUploader
              compact
              label="Upload Photograph"
              testid="gallery-uploader"
              onUploaded={(d) => setGalleryForm(f => ({ ...f, image_url: d.url }))}
            />
            <select className={input} value={galleryForm.category}
              onChange={e => setGalleryForm({ ...galleryForm, category: e.target.value })}
              data-testid="gallery-category" style={inputStyle}>
              <option value="creative">Creative</option>
              <option value="gaming">Gaming</option>
              <option value="second-life">Second Life</option>
              <option value="travel">Travel</option>
              <option value="community">Community</option>
            </select>
            {galleryForm.image_url && (
              <img src={galleryForm.image_url} alt="" className="w-full aspect-video object-cover rounded-sm"
                style={{ border: "1px solid rgba(212,175,55,0.25)" }} />
            )}
            <button type="submit" className="gold-btn" data-testid="gallery-submit">Frame & Hang</button>
          </form>
        </div>
      </div>

      {/* Existing items */}
      <div className="grid lg:grid-cols-2 gap-8 mt-10">
        <div className="parchment-card p-6">
          <h3 className="tavern-heading text-xl gold-text">Existing Entries ({posts.length})</h3>
          <ul className="mt-3 space-y-2 max-h-[420px] overflow-auto">
            {posts.map(p => (
              <li key={p.id} className="flex items-center justify-between gap-3 p-2 border-b"
                style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                <div className="flex items-center gap-3 min-w-0">
                  {p.image_url && (
                    <img src={p.image_url} alt="" className="w-12 h-12 object-cover rounded-sm flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <div className="text-sm tavern-heading truncate" style={{ color: "var(--silver)" }}>{p.title}</div>
                    <div className="text-xs" style={{ color: "var(--muted)" }}>{p.category}</div>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => startEdit(p)}
                    data-testid={`edit-post-${p.id}`}>Edit</button>
                  <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => deletePost(p.id)}
                    data-testid={`delete-post-${p.id}`}
                    style={{ borderColor: "rgba(255,100,100,0.5)", color: "rgba(255,150,150,0.9)" }}>Archive</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="parchment-card p-6">
          <h3 className="tavern-heading text-xl gold-text">Gallery Items ({gallery.length})</h3>
          <ul className="mt-3 grid grid-cols-3 gap-2 max-h-[420px] overflow-auto">
            {gallery.map(g => (
              <li key={g.id} className="relative group">
                <img src={g.image_url} alt={g.title} className="w-full aspect-square object-cover rounded-sm" />
                <button className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: "rgba(0,0,0,0.6)" }}
                  onClick={() => deleteGallery(g.id)}
                  data-testid={`delete-gallery-${g.id}`}>
                  <span className="text-xs" style={{ color: "var(--gold)" }}>Remove</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* CHARACTER PROFILES (Second Life cast) */}
      <div id="character-editor" className="grid lg:grid-cols-[1.2fr_1fr] gap-8 mt-10">
        <div className="parchment-card p-6" data-testid="character-form-card">
          <div className="flex items-center gap-2">
            <UserCircle2 className="h-5 w-5" style={{ color: "var(--gold)" }} />
            <h2 className="tavern-heading text-2xl gold-text">
              {editingCharId ? "Edit Character Profile" : "New Character Profile"}
            </h2>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            Second Life cast. Mark one profile as <em>primary</em> to feature it as the lead. Description supports plain text with line breaks.
          </p>

          <form className="mt-5 space-y-3" onSubmit={submitCharacter}>
            <div className="grid sm:grid-cols-2 gap-3">
              <input className={input} placeholder="Character name (e.g. Thalisse Belmonte)"
                value={characterForm.name}
                onChange={e => setCharacterForm({ ...characterForm, name: e.target.value })}
                required data-testid="character-name" style={inputStyle} />
              <input className={input} placeholder="Role / title (e.g. primary character, the wanderer)"
                value={characterForm.role}
                onChange={e => setCharacterForm({ ...characterForm, role: e.target.value })}
                data-testid="character-role" style={inputStyle} />
            </div>

            <textarea className={input} rows={6}
              placeholder="Description, appearance, established lore. Line breaks are preserved."
              value={characterForm.description}
              onChange={e => setCharacterForm({ ...characterForm, description: e.target.value })}
              data-testid="character-description" style={inputStyle} />

            <div>
              {characterForm.image_url ? (
                <div className="relative" data-testid="character-image-preview">
                  <img src={characterForm.image_url} alt="" className="w-full max-h-72 object-cover rounded-sm"
                    style={{ border: "1px solid rgba(212,175,55,0.25)" }} />
                  <button type="button"
                    className="absolute top-2 right-2 text-xs px-2 py-1 rounded-sm"
                    style={{ background: "rgba(0,0,0,0.6)", color: "var(--gold)" }}
                    onClick={() => setCharacterForm({ ...characterForm, image_url: "" })}
                    data-testid="character-image-clear">clear portrait</button>
                </div>
              ) : (
                <ImageUploader
                  label="Upload Portrait"
                  testid="character-uploader"
                  onUploaded={(d) => setCharacterForm(f => ({ ...f, image_url: d.url }))}
                />
              )}
              <input className={`${input} mt-2 text-xs`}
                placeholder="…or paste a portrait URL"
                value={characterForm.image_url}
                onChange={e => setCharacterForm({ ...characterForm, image_url: e.target.value })}
                data-testid="character-image-url" style={inputStyle} />
            </div>

            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-center">
              <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--silver)" }}>
                <input type="checkbox"
                  checked={characterForm.is_primary}
                  onChange={e => setCharacterForm({ ...characterForm, is_primary: e.target.checked })}
                  data-testid="character-primary"
                  style={{ accentColor: "var(--gold)" }} />
                <Star className="h-4 w-4" style={{ color: "var(--gold)" }} /> Primary character (featured at the top)
              </label>
              <input type="number" className={`${input} w-24`}
                placeholder="Order"
                value={characterForm.order}
                onChange={e => setCharacterForm({ ...characterForm, order: e.target.value })}
                data-testid="character-order" style={inputStyle} />
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" className="gold-btn" data-testid="character-submit">
                {editingCharId ? "Save Changes" : "Add Character"}
              </button>
              {editingCharId && (
                <button type="button" className="gold-btn" onClick={cancelEditCharacter}
                  data-testid="character-cancel"
                  style={{ borderColor: "var(--muted)", color: "var(--muted)" }}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="parchment-card p-6 h-fit" data-testid="character-list-card">
          <h3 className="tavern-heading text-xl gold-text">Character Notebook ({characters.length})</h3>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            Click a character to edit their profile.
          </p>
          <ul className="mt-3 space-y-2 max-h-[520px] overflow-auto">
            {characters.map(c => (
              <li key={c.id} className="flex items-center justify-between gap-3 p-2 rounded-sm border-b"
                style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                <button type="button" className="flex items-center gap-3 min-w-0 text-left"
                  onClick={() => startEditCharacter(c)}
                  data-testid={`edit-character-${c.id}`}>
                  {c.image_url ? (
                    <img src={c.image_url} alt="" className="w-12 h-12 object-cover rounded-sm flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 grid place-items-center rounded-sm flex-shrink-0"
                      style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(212,175,55,0.18)" }}>
                      <UserCircle2 className="h-6 w-6" style={{ color: "rgba(212,175,55,0.4)" }} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm tavern-heading truncate flex items-center gap-1" style={{ color: "var(--silver)" }}>
                      {c.is_primary && <Star className="h-3 w-3 fill-current" style={{ color: "var(--gold)" }} />}
                      {c.name}
                    </div>
                    <div className="text-xs truncate" style={{ color: "var(--muted)" }}>{c.role || "—"}</div>
                  </div>
                </button>
                <button className="gold-btn !py-1 !px-3 text-xs"
                  onClick={() => deleteCharacter(c.id)}
                  data-testid={`delete-character-${c.id}`}
                  style={{ borderColor: "rgba(255,100,100,0.5)", color: "rgba(255,150,150,0.9)" }}>Remove</button>
              </li>
            ))}
            {!characters.length && (
              <li className="text-sm" style={{ color: "var(--muted)" }}>No characters yet — add the first one to the left.</li>
            )}
          </ul>
        </div>
      </div>

      {/* GAME LIBRARY (Adventures) */}
      <div id="game-editor" className="grid lg:grid-cols-[1.2fr_1fr] gap-8 mt-10">
        <div className="parchment-card p-6" data-testid="game-form-card">
          <div className="flex items-center gap-2">
            <Gamepad2 className="h-5 w-5" style={{ color: "var(--gold)" }} />
            <h2 className="tavern-heading text-2xl gold-text">
              {editingGameId ? "Edit Game" : "Add a Game"}
            </h2>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            Manage the Adventures library — add, rate, set a status, and reorder. Drag-free reordering via the arrows on the right.
          </p>

          <form className="mt-5 space-y-3" onSubmit={submitGame}>
            <input className={input} placeholder="Title (e.g. Baldur's Gate 3)"
              value={gameForm.name}
              onChange={e => setGameForm({ ...gameForm, name: e.target.value })}
              required data-testid="game-name" style={inputStyle} />

            <select className={input} value={gameForm.status}
              onChange={e => setGameForm({ ...gameForm, status: e.target.value })}
              data-testid="game-status" style={inputStyle}>
              {GAME_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>

            <div className="flex items-center gap-3">
              <span className="page-eyebrow" style={{ fontSize: "0.7rem" }}>Rating</span>
              <div className="flex items-center gap-1" data-testid="game-rating">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} type="button" aria-label={`${n} stars`}
                    onClick={() => setGameForm({ ...gameForm, rating: gameForm.rating === n ? 0 : n })}
                    data-testid={`game-rating-${n}`}
                    className="p-0.5">
                    <Star className="h-5 w-5" style={{ color: "var(--gold)" }}
                      fill={n <= (gameForm.rating || 0) ? "currentColor" : "none"} />
                  </button>
                ))}
                {!!gameForm.rating && (
                  <button type="button" className="text-xs underline ml-1" style={{ color: "var(--muted)" }}
                    onClick={() => setGameForm({ ...gameForm, rating: 0 })}>clear</button>
                )}
              </div>
            </div>

            <textarea className={input} rows={4}
              placeholder="Notes (thoughts, review, where you're at…)"
              value={gameForm.review}
              onChange={e => setGameForm({ ...gameForm, review: e.target.value })}
              data-testid="game-review" style={inputStyle} />

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="page-eyebrow" style={{ fontSize: "0.7rem" }}>Cover Image (optional)</label>
                {gameForm.image_url && (
                  <button type="button" className="text-xs underline" style={{ color: "var(--muted)" }}
                    onClick={() => setGameForm({ ...gameForm, image_url: "" })}>clear</button>
                )}
              </div>
              {gameForm.image_url ? (
                <img src={gameForm.image_url} alt="" className="w-full max-h-56 object-cover rounded-sm"
                  style={{ border: "1px solid rgba(212,175,55,0.25)" }} data-testid="game-image-preview" />
              ) : (
                <ImageUploader compact label="Upload Cover" testid="game-uploader"
                  onUploaded={(d) => setGameForm(f => ({ ...f, image_url: d.url }))} />
              )}
              <input className={`${input} mt-2 text-xs`}
                placeholder="…or paste a cover image URL"
                value={gameForm.image_url}
                onChange={e => setGameForm({ ...gameForm, image_url: e.target.value })}
                data-testid="game-image-url" style={inputStyle} />
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" className="gold-btn" data-testid="game-submit">
                {editingGameId ? "Save Changes" : "Add Game"}
              </button>
              {editingGameId && (
                <button type="button" className="gold-btn" onClick={cancelEditGame} data-testid="game-cancel"
                  style={{ borderColor: "var(--muted)", color: "var(--muted)" }}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="parchment-card p-6 h-fit" data-testid="game-list-card">
          <h3 className="tavern-heading text-xl gold-text">Adventures Library ({games.length})</h3>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>Use the arrows to reorder how games appear on the Adventures page.</p>
          <ul className="mt-3 space-y-2 max-h-[560px] overflow-auto">
            {games.map((g, i) => (
              <li key={g.id} className="flex items-center justify-between gap-3 p-2 rounded-sm border-b"
                style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex flex-col">
                    <button type="button" className="p-0.5 disabled:opacity-30" disabled={i === 0}
                      onClick={() => moveGame(i, -1)} data-testid={`game-up-${g.id}`} aria-label="Move up">
                      <ArrowUp className="h-4 w-4" style={{ color: "var(--gold)" }} />
                    </button>
                    <button type="button" className="p-0.5 disabled:opacity-30" disabled={i === games.length - 1}
                      onClick={() => moveGame(i, 1)} data-testid={`game-down-${g.id}`} aria-label="Move down">
                      <ArrowDown className="h-4 w-4" style={{ color: "var(--gold)" }} />
                    </button>
                  </div>
                  {g.image_url ? (
                    <img src={g.image_url} alt="" className="w-12 h-12 object-cover rounded-sm flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 grid place-items-center rounded-sm flex-shrink-0"
                      style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(212,175,55,0.18)" }}>
                      <Gamepad2 className="h-5 w-5" style={{ color: "rgba(212,175,55,0.4)" }} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm tavern-heading truncate" style={{ color: "var(--silver)" }}>{g.name}</div>
                    <div className="text-xs flex items-center gap-1" style={{ color: "var(--muted)" }}>
                      <span>{statusLabel(g.status)}</span>
                      {!!g.rating && <span style={{ color: "var(--gold)" }}>· {"★".repeat(g.rating)}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => startEditGame(g)}
                    data-testid={`edit-game-${g.id}`}>Edit</button>
                  <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => deleteGame(g.id)}
                    data-testid={`delete-game-${g.id}`}
                    style={{ borderColor: "rgba(255,100,100,0.5)", color: "rgba(255,150,150,0.9)" }}>Remove</button>
                </div>
              </li>
            ))}
            {!games.length && (
              <li className="text-sm" style={{ color: "var(--muted)" }}>No games yet — add the first one to the left.</li>
            )}
          </ul>
        </div>
      </div>

      {/* NOCTURNE RADIO SETLIST */}
      <div id="radio-editor" className="grid lg:grid-cols-[1.2fr_1fr] gap-8 mt-10">
        <div className="parchment-card p-6" data-testid="radio-form-card">
          <div className="flex items-center gap-2">
            <RadioIcon className="h-5 w-5" style={{ color: "var(--gold)" }} />
            <h2 className="tavern-heading text-2xl gold-text">
              {editingTrackId ? "Edit Broadcast" : "Add a Broadcast"}
            </h2>
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
            Upload an MP3 and it plays in the Nocturne Radio player. Reorder the setlist with the arrows on the right.
          </p>

          <form className="mt-5 space-y-3" onSubmit={submitTrack}>
            <input className={input} placeholder="Track title"
              value={trackForm.title}
              onChange={e => setTrackForm({ ...trackForm, title: e.target.value })}
              required data-testid="track-title" style={inputStyle} />

            <input className={input} placeholder="Artist name"
              value={trackForm.dj}
              onChange={e => setTrackForm({ ...trackForm, dj: e.target.value })}
              data-testid="track-dj" style={inputStyle} />

            <textarea className={input} rows={4}
              placeholder="Description / field note"
              value={trackForm.lore}
              onChange={e => setTrackForm({ ...trackForm, lore: e.target.value })}
              data-testid="track-lore" style={inputStyle} />

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="page-eyebrow" style={{ fontSize: "0.7rem" }}>Audio File (MP3)</label>
                {trackForm.audio_url && (
                  <button type="button" className="text-xs underline" style={{ color: "var(--muted)" }}
                    onClick={() => setTrackForm({ ...trackForm, audio_url: "" })}>clear</button>
                )}
              </div>
              {trackForm.audio_url ? (
                <div className="space-y-2" data-testid="track-audio-preview">
                  <div className="flex items-center gap-2 text-xs" style={{ color: "var(--gold)" }}>
                    <Music className="h-4 w-4" /> Audio attached
                  </div>
                  <audio controls src={trackForm.audio_url} className="w-full" style={{ height: 36 }} />
                </div>
              ) : (
                <AudioUploader label="Upload MP3" testid="track-uploader"
                  onUploaded={(d) => setTrackForm(f => ({ ...f, audio_url: d.url }))} />
              )}
              <input className={`${input} mt-2 text-xs`}
                placeholder="…or paste an audio URL"
                value={trackForm.audio_url}
                onChange={e => setTrackForm({ ...trackForm, audio_url: e.target.value })}
                data-testid="track-audio-url" style={inputStyle} />
            </div>

            <div className="flex flex-wrap gap-3">
              <button type="submit" className="gold-btn" data-testid="track-submit">
                {editingTrackId ? "Save Changes" : "Add Track"}
              </button>
              {editingTrackId && (
                <button type="button" className="gold-btn" onClick={cancelEditTrack} data-testid="track-cancel"
                  style={{ borderColor: "var(--muted)", color: "var(--muted)" }}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="parchment-card p-6 h-fit" data-testid="radio-list-card">
          <h3 className="tavern-heading text-xl gold-text">Tonight's Setlist ({tracks.length})</h3>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>Use the arrows to reorder the broadcast.</p>
          <ul className="mt-3 space-y-2 max-h-[560px] overflow-auto">
            {tracks.map((t, i) => (
              <li key={t.id} className="flex items-center justify-between gap-3 p-2 rounded-sm border-b"
                style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="flex flex-col">
                    <button type="button" className="p-0.5 disabled:opacity-30" disabled={i === 0}
                      onClick={() => moveTrack(i, -1)} data-testid={`track-up-${t.id}`} aria-label="Move up">
                      <ArrowUp className="h-4 w-4" style={{ color: "var(--gold)" }} />
                    </button>
                    <button type="button" className="p-0.5 disabled:opacity-30" disabled={i === tracks.length - 1}
                      onClick={() => moveTrack(i, 1)} data-testid={`track-down-${t.id}`} aria-label="Move down">
                      <ArrowDown className="h-4 w-4" style={{ color: "var(--gold)" }} />
                    </button>
                  </div>
                  <div className="w-10 h-10 grid place-items-center rounded-sm flex-shrink-0"
                    style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(212,175,55,0.18)" }}>
                    {t.audio_url
                      ? <Music className="h-4 w-4" style={{ color: "var(--gold)" }} />
                      : <Music className="h-4 w-4" style={{ color: "rgba(212,175,55,0.35)" }} />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm tavern-heading truncate" style={{ color: "var(--silver)" }}>{i + 1}. {t.title}</div>
                    <div className="text-xs truncate" style={{ color: "var(--muted)" }}>
                      {t.dj || "—"}{t.audio_url ? " · audio ✓" : " · no audio"}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => startEditTrack(t)}
                    data-testid={`edit-track-${t.id}`}>Edit</button>
                  <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => deleteTrack(t.id)}
                    data-testid={`delete-track-${t.id}`}
                    style={{ borderColor: "rgba(255,100,100,0.5)", color: "rgba(255,150,150,0.9)" }}>Remove</button>
                </div>
              </li>
            ))}
            {!tracks.length && (
              <li className="text-sm" style={{ color: "var(--muted)" }}>No tracks yet — add the first broadcast to the left.</li>
            )}
          </ul>
        </div>
      </div>

      {/* TRAVELER'S GUEST BOOK (moderation-ready) */}
      <div id="guestbook-admin" className="parchment-card p-6 mt-10" data-testid="guestbook-admin-card">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5" style={{ color: "var(--gold)" }} />
          <h2 className="tavern-heading text-2xl gold-text">Traveler's Guest Book ({guestbook.length})</h2>
        </div>
        <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
          Every signature left by visitors. Archive hides an entry from the public book (soft delete); delete removes it permanently.
        </p>
        <ul className="mt-4 space-y-3 max-h-[620px] overflow-auto">
          {guestbook.map((g) => (
            <li key={g.id} className="p-3 rounded-sm border-b flex items-start justify-between gap-3"
              style={{ borderColor: "rgba(212,175,55,0.15)", background: g.is_archived ? "rgba(0,0,0,0.25)" : "transparent", opacity: g.is_archived ? 0.7 : 1 }}
              data-testid={`gb-admin-${g.id}`}>
              <div className="flex items-start gap-3 min-w-0">
                {g.avatar_url ? (
                  <img src={g.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    style={{ border: "1px solid rgba(212,175,55,0.3)" }} />
                ) : (
                  <div className="w-10 h-10 grid place-items-center rounded-full flex-shrink-0"
                    style={{ background: "rgba(0,0,0,0.3)", border: "1px solid rgba(212,175,55,0.25)" }}>
                    <UserCircle2 className="h-5 w-5" style={{ color: "rgba(212,175,55,0.5)" }} />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm tavern-heading" style={{ color: "var(--silver)" }}>{g.display_name}</span>
                    <span className="text-xs flex items-center gap-1" style={{ color: "var(--orchid-light)" }}>
                      <MapPin className="h-3 w-3" /> {g.traveling_from}
                    </span>
                    {g.is_archived && (
                      <span className="text-[0.6rem] uppercase tracking-widest px-2 py-0.5 rounded-sm"
                        style={{ background: "rgba(0,0,0,0.5)", color: "var(--muted)" }}>archived</span>
                    )}
                  </div>
                  <div className="text-[0.7rem]" style={{ color: "var(--muted)" }}>
                    {new Date(g.created_at).toLocaleString()}
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-wrap" style={{ color: "var(--muted)" }}>{g.message}</p>
                </div>
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                {g.is_archived ? (
                  <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => unarchiveEntry(g.id)}
                    data-testid={`gb-unarchive-${g.id}`}>
                    <ArchiveRestore className="h-3 w-3" /> Restore
                  </button>
                ) : (
                  <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => archiveEntry(g.id)}
                    data-testid={`gb-archive-${g.id}`}>
                    <Archive className="h-3 w-3" /> Archive
                  </button>
                )}
                <button className="gold-btn !py-1 !px-3 text-xs" onClick={() => deleteEntry(g.id)}
                  data-testid={`gb-delete-${g.id}`}
                  style={{ borderColor: "rgba(255,100,100,0.5)", color: "rgba(255,150,150,0.9)" }}>Delete</button>
              </div>
            </li>
          ))}
          {!guestbook.length && (
            <li className="text-sm" style={{ color: "var(--muted)" }}>No guest book entries yet.</li>
          )}
        </ul>
      </div>

      {/* Insert image into article dialog */}
      {insertOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4"
             style={{ background: "rgba(0,0,0,0.78)" }}
             onClick={() => setInsertOpen(false)}
             data-testid="insert-image-dialog">
          <div className="parchment-card w-full max-w-lg p-7 relative" onClick={e => e.stopPropagation()}>
            <button className="absolute top-3 right-3 p-2" onClick={() => setInsertOpen(false)} aria-label="Close" data-testid="insert-image-close">
              <X style={{ color: "var(--gold)" }} />
            </button>
            <h3 className="tavern-heading text-2xl gold-text">Insert Image Into Article</h3>
            <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>Drop a file or paste any image URL. It will be inserted at your cursor.</p>

            <div className="mt-5">
              <ImageUploader
                label="Upload Image"
                testid="inline-uploader"
                onUploaded={(d) => {
                  insertMarkdownImage(d.url, d.filename || "image");
                  setInsertOpen(false);
                }}
              />
            </div>

            <div className="divider-ornament tavern-heading mt-5 text-sm"><span>or</span></div>

            <form
              className="flex flex-col sm:flex-row gap-2 mt-3"
              onSubmit={(e) => {
                e.preventDefault();
                const url = e.target.url.value.trim();
                const alt = e.target.alt.value.trim() || "image";
                if (!url) return;
                insertMarkdownImage(url, alt);
                setInsertOpen(false);
              }}
            >
              <input name="url" className={input} placeholder="https://image-url" style={inputStyle} required data-testid="inline-url" />
              <input name="alt" className={input + " sm:max-w-[160px]"} placeholder="Alt text" style={inputStyle} data-testid="inline-alt" />
              <button type="submit" className="gold-btn" data-testid="inline-url-insert">Insert</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
